import { useRouteBodyState } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import type { SentenceExample } from "@-/dev.improve.haniverse.lib.sentence";
import {
  getInTabMorphemePanelsHref,
  MorphemeInfoPanelContext,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content._context";
import { stringLength } from "@-/com.npmjs.string-length";
import { cn } from "@-/com.npmjs.cn";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { toDiacriticPinyin } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import { useSentenceSpeech } from "@-/dev.improve.haniverse.lib.sentence.speech";
import { useSentenceWords } from "@-/dev.improve.haniverse.lib.sentence.words";
import { useMorphemePanelsHrefNavigator } from "@-/dev.improve.haniverse.lib.morpheme.page.navigator";
import { hasHanScript } from "@-/dev.improve.haniverse.lib.unicode";
import { NavContext } from "@-/dev.improve.haniverse.routes.nav._context";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { type ReactNode, useCallback, useMemo } from "react";
import { ActivityIndicator, PanResponder, Pressable, View } from "@-/com.npmjs.react-native";

function SentenceRowActionButton({
  accessibilityLabel,
  active = false,
  busy = false,
  iconName,
  onPress,
  variant = "filled",
}: {
  accessibilityLabel: string;
  active?: boolean;
  /** Work is in flight (a neural voice synthesizing): a spinner replaces the icon. */
  busy?: boolean;
  iconName: React.ComponentProps<typeof Icon>["name"];
  onPress: () => void;
  variant?: "filled" | "ghost";
}) {
  return (
    <Pressable
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      accessibilityState={{ busy, selected: active }}
      className={cn(
        "items-center justify-center rounded-full",
        variant === "ghost" ? "size-8" : "size-6",
        !busy && variant === "filled" && (active ? "bg-primary/10" : "bg-muted/40"),
      )}
      hitSlop={6}
      onPress={onPress}
    >
      {busy ? (
        <ActivityIndicator size="small" />
      ) : (
        <Icon
          className={active ? "text-primary" : "text-muted-foreground"}
          name={iconName}
          size={variant === "ghost" ? 18 : 14}
        />
      )}
    </Pressable>
  );
}

export function SentenceRow({
  expanded,
  onNavigate,
  onToggleExpanded,
  sentence,
  showAllDetails = false,
  showTopDivider = false,
  underlinedWord,
}: {
  /** Controlled accordion mode: the chevron reveals translation and reading-aid controls. */
  expanded?: boolean;
  onNavigate?: () => void;
  onToggleExpanded?: () => void;
  sentence: SentenceExample;
  /** The all-examples browser shows both reading aids independently of device preferences. */
  showAllDetails?: boolean;
  showTopDivider?: boolean;
  underlinedWord?: string;
}) {
  const [morphemeHrefObject] = useContextSelectors(
    MorphemeInfoPanelContext,
    (c) => c.morphemeHrefObject,
  );
  const cardPageNavigator = useMorphemePanelsHrefNavigator();

  // Pinyin and translation follow the global defaults (toggled from the section header) until
  // this sentence's own button overrides them.
  const [isPinyinShownByDefault] = useDeviceSetting("sentencePinyinShownByDefault");
  const [isTranslationShownByDefault] = useDeviceSetting("sentenceTranslationShownByDefault");
  const [pinyinOverride, setPinyinOverride] = useRouteBodyState<boolean | null>(
    `sentence:${sentence.sentence}:pinyin`,
    null,
  );
  const [translationOverride, setTranslationOverride] = useRouteBodyState<boolean | null>(
    `sentence:${sentence.sentence}:translation`,
    null,
  );
  const [showChunks, setShowChunks] = useRouteBodyState(
    `sentence:${sentence.sentence}:chunks`,
    false,
  );
  const isAccordion = expanded !== undefined;
  const isChunksShown = showChunks && (!isAccordion || expanded);
  const isPinyinShown =
    showAllDetails ||
    (isAccordion
      ? expanded && (pinyinOverride ?? false)
      : (pinyinOverride ?? isPinyinShownByDefault));
  const isTranslationShown =
    showAllDetails ||
    (isAccordion ? expanded : (translationOverride ?? isTranslationShownByDefault));

  const togglePinyin = useCallback(() => {
    setPinyinOverride(!isPinyinShown);
  }, [isPinyinShown, setPinyinOverride]);
  const toggleTranslation = useCallback(() => {
    setTranslationOverride(!isTranslationShown);
  }, [isTranslationShown, setTranslationOverride]);
  // Sentences have no recorded audio, so read-aloud goes through the learner's chosen voice (a
  // downloaded neural voice or the platform's TTS) rather than the bundled per-syllable pinyin
  // clips that `PinyinSoundButton` plays; see `lib/sentence/speech`.
  const [isMutedOverride] = useContextSelectors(NavContext, (c) => c.isMutedOverride);
  const {
    isPreparing,
    isSpeaking,
    toggle: toggleSound,
  } = useSentenceSpeech(sentence.sentence, {
    isMuted: isMutedOverride,
  });
  const audioLabel = isSpeaking || isPreparing ? "Stop sentence audio" : "Play sentence audio";
  const audioGesture = useMemo(
    () =>
      PanResponder.create({
        // Claim only a deliberate rightward drag; taps, holds and vertical scrolling pass through.
        onMoveShouldSetPanResponderCapture: (_event, { dx, dy }) =>
          dx > 12 && dx > Math.abs(dy) * 1.5,
        onPanResponderRelease: (_event, { dx, dy }) => {
          if (dx >= 36 && dx > Math.abs(dy) * 1.5) {
            toggleSound();
          }
        },
        onPanResponderTerminationRequest: () => false,
      }),
    [toggleSound],
  );

  const handleWordPress = useCallback(
    (word: string) => {
      // Open the word/character in the current tab's reference layout: Explore inside Learn,
      // Browse inside Browse, or the corresponding Decks/Collections stack. Resolve the bare
      // route rather than carrying the current panel's morpheme into the new destination.
      // Single characters open to the Mnemonics panel when reached from Learn (its back defaults
      // to mnemonics) and the Info panel otherwise; multi-character words only have a
      // info view, so they always land there.
      const isLearnContext = morphemeHrefObject.href.includes("/learn/");
      onNavigate?.();
      cardPageNavigator.push.mutate({
        morpheme: word,
        prefix: {
          href: getInTabMorphemePanelsHref(morphemeHrefObject.href),
          params: stringLength(word) === 1 ? { view: isLearnContext ? "mnemonics" : "info" } : {},
        },
      });
    },
    [cardPageNavigator, morphemeHrefObject.href, onNavigate],
  );

  // Jieba segments the sentence, then tokens are aligned to words in the static DB. The vocabulary is
  // queried per-sentence (no longer bundled), so `words` is undefined until the lookup resolves.
  const { words } = useSentenceWords(sentence.sentence);

  // Match the full sentence so every occurrence is marked, even when Jieba splits
  // the target across tokens or includes it inside a larger token.
  const underlinedPositions = useMemo(() => {
    const positions = new Set<number>();
    if (underlinedWord) {
      let start = sentence.sentence.indexOf(underlinedWord);
      while (start !== -1) {
        for (let offset = 0; offset < underlinedWord.length; offset += 1) {
          positions.add(start + offset);
        }
        start = sentence.sentence.indexOf(underlinedWord, start + 1);
      }
    }
    return positions;
  }, [sentence.sentence, underlinedWord]);

  function renderSentenceText(
    text: string,
    sentenceOffset: number,
    weightClassName = "font-normal",
  ) {
    const runs: ReactNode[] = [];
    let start = 0;
    while (start < text.length) {
      const underlined = underlinedPositions.has(sentenceOffset + start);
      let end = start + 1;
      while (end < text.length && underlinedPositions.has(sentenceOffset + end) === underlined) {
        end += 1;
      }
      const run = text.slice(start, end);
      runs.push(
        underlined ? (
          <Text className={cn("leading-6 text-foreground underline", weightClassName)} key={start}>
            {run}
          </Text>
        ) : (
          run
        ),
      );
      start = end;
    }
    return runs;
  }

  let sentenceOffset = 0;

  return (
    <View {...audioGesture.panHandlers} testID={`sentence-audio-swipe-${sentence.id}`}>
      {showTopDivider ? <View className="border-t border-border" /> : null}
      {/* Accordion headers keep the sentence compact; reading aids are revealed underneath. */}
      <View className="flex-row items-start gap-x-2 px-3 py-2">
        {isAccordion ? (
          <Pressable
            accessibilityLabel={`${expanded ? "Collapse" : "Expand"} sentence: ${sentence.sentence}`}
            accessibilityRole="button"
            accessibilityState={{ expanded }}
            className="size-8 items-center justify-center"
            hitSlop={6}
            onPress={onToggleExpanded}
          >
            <Icon
              className="text-muted-foreground"
              name={expanded ? "LuChevronUp" : "LuChevronDown"}
              size={18}
            />
          </Pressable>
        ) : null}
        <View className="min-w-0 flex-1 gap-y-0.5">
          <View className="min-h-8 justify-center">
            <Text
              accessibilityActions={[{ label: audioLabel, name: "sentence-audio" }]}
              accessibilityHint="Swipe right to play or stop sentence audio"
              className="text-base leading-6 font-normal text-foreground"
              onAccessibilityAction={({ nativeEvent }) => {
                if (nativeEvent.actionName === "sentence-audio") {
                  toggleSound();
                }
              }}
              selectable
            >
              {isChunksShown && words !== undefined
                ? words.map(({ word }, index) => {
                    const wordOffset = sentenceOffset;
                    sentenceOffset += word.length;
                    const weightClassName = index % 2 === 0 ? "font-normal" : "font-semibold";
                    return (
                      <Text
                        className={cn("leading-6", weightClassName)}
                        key={`${index}-${word}`}
                        onPress={hasHanScript(word) ? () => handleWordPress(word) : undefined}
                      >
                        {renderSentenceText(word, wordOffset, weightClassName)}
                      </Text>
                    );
                  })
                : renderSentenceText(sentence.sentence, 0)}
            </Text>
          </View>

          {isPinyinShown && words !== undefined ? (
            <Text className="pt-1 text-xs leading-5 text-muted-foreground" selectable>
              {words
                .map(({ pinyin, word }) => (pinyin === null ? word : toDiacriticPinyin(pinyin)))
                .join(" ")}
            </Text>
          ) : null}
          {isTranslationShown && sentence.translation !== null ? (
            <Text className="text-xs text-muted-foreground" selectable>
              {sentence.translation}
            </Text>
          ) : null}
          {isAccordion && expanded ? (
            <View className="flex-row items-center gap-x-2 pt-1">
              <Pressable
                accessibilityLabel={isPinyinShown ? "Hide pinyin" : "Show pinyin"}
                accessibilityRole="button"
                accessibilityState={{ selected: isPinyinShown }}
                className="p-1"
                onPress={togglePinyin}
              >
                <Text className="text-xs text-muted-foreground">
                  {isPinyinShown ? "Hide pinyin" : "Show pinyin"}
                </Text>
              </Pressable>
              <Pressable
                accessibilityLabel={showChunks ? "Hide sentence chunks" : "Show sentence chunks"}
                accessibilityRole="button"
                accessibilityState={{ selected: showChunks }}
                className="p-1"
                onPress={() => setShowChunks((value) => !value)}
              >
                <Text className="text-xs text-muted-foreground">
                  {showChunks ? "Hide chunks" : "Show chunks"}
                </Text>
              </Pressable>
            </View>
          ) : null}
        </View>

        {isAccordion ? (
          <SentenceRowActionButton
            accessibilityLabel={audioLabel}
            active={isSpeaking}
            busy={isPreparing}
            iconName="IoVolumeHighOutline"
            onPress={toggleSound}
            variant="ghost"
          />
        ) : (
          <View className="flex-row items-center gap-x-1">
            <SentenceRowActionButton
              accessibilityLabel={showChunks ? "Hide sentence chunks" : "Show sentence chunks"}
              active={showChunks}
              iconName="LuWholeWord"
              onPress={() => setShowChunks((value) => !value)}
            />
            <SentenceRowActionButton
              accessibilityLabel={
                isSpeaking || isPreparing ? "Stop sentence audio" : "Play sentence audio"
              }
              active={isSpeaking}
              busy={isPreparing}
              iconName="IoVolumeHighOutline"
              onPress={toggleSound}
            />
            {showAllDetails ? null : (
              <SentenceRowActionButton
                accessibilityLabel="Toggle pinyin"
                active={isPinyinShown}
                iconName="MsLanguageChinesePinyin"
                onPress={togglePinyin}
              />
            )}
            {showAllDetails || sentence.translation === null ? null : (
              <SentenceRowActionButton
                accessibilityLabel="Toggle translation"
                active={isTranslationShown}
                iconName="MdLanguage"
                onPress={toggleTranslation}
              />
            )}
          </View>
        )}
      </View>
    </View>
  );
}
